import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import useDeal from 'src/hooks/api/useDeal';
import { MdContentCopy as CopyIcon } from '@react-icons/all-files/md/MdContentCopy';
import { SITE_URL } from '../../../../../../env';
import { useNotifier } from '../../../../../../components/NotificationCenter/AlertNotificationCenter';
import ProviderSelect from '../../../../../../components/ApiField/ProviderSelect';

interface Props {
  providerId?: string;
  dealId?: number;
  open: boolean;
  onClose: () => void;
}

const ExternalLinkDialog: FC<Props> = ({ dealId, open, onClose, ...props }) => {
  const { addNotification } = useNotifier();

  const [error, setError] = useState<string>();
  const [providerId, setProviderId] = useState<string | undefined>(props.providerId);
  const [deliveryOrderToken, setDeliveryOrderToken] = useState<string>();
  const { getDeliveryOrderToken } = useDeal();

  useEffect(() => {
    if (dealId && open) {
      setError(undefined);

      (async () => {
        try {
          if (!providerId) {
            throw new Error('Veuillez sélectionner un prestataire.');
          }

          const deliveryOrderToken = await getDeliveryOrderToken(dealId, providerId);
          setDeliveryOrderToken(deliveryOrderToken);
        } catch (e) {
          setError(e.message || 'Une erreur est survenue.');
        }
      })();
    }
  }, [providerId, dealId, open]);

  const getProviderLink = useCallback((): string => {
    return `${SITE_URL}/delivery-order?token=${deliveryOrderToken}`;
  }, [deliveryOrderToken]);

  const copyProviderLinkToClipboard = useCallback(() => {
    navigator.clipboard.writeText(getProviderLink());
    addNotification({
      title: 'Lien copié dans le presse-papier !',
      alertProps: { color: 'success' },
    });
  }, [getProviderLink]);

  return (
    <Dialog open={open} onClose={onClose} scroll='paper'>
      <DialogTitle>Lien de partage</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            Lien de partage pour les prestataires externes.
          </Grid>

          <Grid item xs={12}>
            <ProviderSelect value={providerId} onChange={setProviderId} />
          </Grid>

          <Grid item xs={12}>
            {deliveryOrderToken && (
              <>
                <TextField
                  label='Création du bon de livraison'
                  value={getProviderLink()}
                  onChange={undefined}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton onClick={copyProviderLinkToClipboard}>
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant='caption'>Le lien est valable 14 jours.</Typography>
              </>
            )}
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Box fontSize='0.8rem' color='error.main'>
                {error}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalLinkDialog;
